import React, { useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import { useForm } from 'react-hook-form';
import {
  Routes, Route, useLocation,
  useParams,
  useNavigate,
  NavLink,
} from 'react-router-dom';
import { format } from 'date-fns';
import { CiCircleCheck } from 'react-icons/ci';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import ColorScale from 'color-scales';
import { BiPlus } from 'react-icons/bi';
import { IoChevronDown } from 'react-icons/io5';
import styles from '../estimate/estimate.module.scss';
import {
  useGetEtaiement, useCreateEtaiementPhase,
  useGetEtaiementsList,
  useUpdateEtaiement,
  useDuplicateEtaiementPhase,
  useReorderEtaiementPhases,
} from '~/hooks/etaiements';

import DraggablePhaseTabs from '~/components/shared/DraggablePhaseTabs';
import PhaseHeaderRates from '~/components/shared/PhaseHeaderRates';

import Loader from '~/components/Loader';
import GeneralSidebar from '~/components/etaiement/GeneralSidebar';
import PhaseSidebar from '~/components/etaiement/PhaseSidebar';

import GeneralInfos from './general-informations';
import Phase from './phase/index';
import BackButton from '~/components/BackButton';

import {
  StyledSelect,
} from '~/lib/HooksFormFields';
import { IPhase } from '~/types/estimates';
import { useUserProfile } from '~/hooks/auth';
import { IOption } from '~/types/options';

const colorScale = new ColorScale(0, 100, ['#EAEDFA', '#acaeb6']);

const Etaiement = () => {
  const { id, ...params } = useParams();
  const location = useLocation();
  const [addStep, setAddStep] = useState<boolean>(false);
  const [addStepDown, setAddStepDown] = useState<boolean>(false);
  const [selectStep, setSelectStep] = useState<boolean>(false);
  const navigate = useNavigate();

  const { data: userAuth } = useUserProfile();

  const {
    isLoading,
    data: etaiement,
  } = useGetEtaiement(id || '');

  const countPhases = etaiement?.phases?.length || 0;

  const currentPhase = useMemo(() => {
    const found = etaiement?.phases.find((d: IPhase) => d._id === params['*']);
    return found;
  }, [etaiement, params]);

  const {
    mutateAsync: createEtaiementPhase,
  } = useCreateEtaiementPhase();

  const {
    mutateAsync: duplicateEtaiementPhase,
  } = useDuplicateEtaiementPhase();

  const { mutateAsync: reorderEtaiementPhases } = useReorderEtaiementPhases();

  const {
    control,
    getValues,
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      status: etaiement?.status,
    },
  });

  const { data: list = {} } = useGetEtaiementsList();
  const { status } = list;
  const {
    mutate: updateEtaiement,
  } = useUpdateEtaiement('Les modifications ont été prise en compte');

  const etaiementStatus = watch('status') || etaiement?.status;
  const canEditSentStatus = etaiementStatus === 'sent' || etaiementStatus === 'archived';
  const sentStatusOptions = useMemo(
    () => status?.filter((s: IOption) => (
      s.value === 'sent'
      || s.value === 'refused'
      || s.value === 'validated'
      || s.value === 'archived'
    )),
    [status],
  );

  const isPreview = etaiement?.status === 'validated'
    || etaiement?.status === 'sent'
    || etaiement?.status === 'refused'
    || etaiement?.status === 'archived'
    || (etaiement?.status === 'review-pending' && etaiement.author?._id === userAuth?._id);

  useEffect(() => {
    if (!getValues('status') && etaiement?.status) {
      setValue('status', etaiement.status);
    }
  }, [etaiement]);

  const generalInfosActive = useMemo(() => (
    location.pathname.split('/').length < 4
  ), [location.pathname]);

  const handleCreatePhase = async () => {
    if (!id) return;
    try {
      const { etaiement: updated } = await createEtaiementPhase({ etaiementId: id });
      const phases: IPhase[] = updated?.phases || [];
      navigate(`/etaiements/${id}/${phases[phases.length - 1]._id}`);
      const layout = document?.getElementById('layout');
      if (layout) {
        layout.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    } catch {
      toast.error('La phase n\'a pu être créé');
    }
  };

  const handleDuplicatePhase = async () => {
    if (!id || !currentPhase?._id) return;
    try {
      const { etaiement: updated } = await duplicateEtaiementPhase({
        etaiementId: id,
        phaseId: currentPhase?._id,
      });
      const phases: IPhase[] = updated?.phases || [];
      navigate(`/etaiements/${id}/${phases[phases.length - 1]._id}`);
      const layout = document?.getElementById('layout');
      if (layout) {
        layout.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    } catch {
      toast.error('La phase n\'a pu être créé');
    }
  };

  const getIsActive = (pathname: string) => {
    let active = false;
    if (location.pathname === `/etaiements/${id}${pathname}`) active = true;
    return active;
  };

  const handleReorderPhases = async (phaseIds: string[]) => {
    if (!id || isPreview) return;
    await reorderEtaiementPhases({ etaiementId: id, phaseIds });
  };

  useEffect(() => {
    if (etaiement?._id) {
      reset(etaiement);
    }
  }, [etaiement]);

  return (
    <div className={styles.estimate}>
      {isLoading && (
        <div className={styles.loader}><Loader /></div>
      )}
      {etaiement && (
        <div className={styles.content}>
          <StickyBox>
            <div className={styles.sidebar}>
              <div className={styles.back}>
                <BackButton path='/estimates/etaiements' />
                <NavLink to='/estimates/etaiements'>Retour à la liste des devis étaiement</NavLink>
              </div>
              <div className={styles.updatedAt}>
                {CiCircleCheck({ size: 20 })}
                <p>Dernière modification {format(etaiement.updatedAt, "dd.MM.yyyy à HH'h'mm", { locale: fr })}</p>
              </div>
              <div className={styles.status}>
                <label>Statut</label>
                <StyledSelect
                  name='status'
                  control={control}
                  options={canEditSentStatus ? sentStatusOptions : status}
                  isDisabled={!canEditSentStatus}
                  handleChange={(opt:any) => {
                    updateEtaiement({ _id: etaiement?._id, status: opt.value });
                  }}
                  withColorDot
                />
              </div>
              <hr />
              {generalInfosActive
                ? <GeneralSidebar
                  etaiement={etaiement}
                  isPreview={isPreview}
                />
                : <PhaseSidebar
                  isPreview={isPreview}
                />
              }
            </div>
          </StickyBox>
          <div className={styles.containers}>
            <div className={styles.navTabs}>
              <NavLink
                to=''
                style={{
                  position: 'relative',
                  zIndex: (etaiement?.phases?.length || 0) + 101,
                  backgroundColor: getIsActive('') ? 'white' : colorScale.getColor(0).toRGBString(),
                }}
              >
                Informations
              </NavLink>
              {countPhases >= 3
                && <div className={styles.step}>
                {!selectStep
                  ? <a
                    onMouseOver={() => {
                      setSelectStep(true);
                    }}
                    className={currentPhase?.name ? styles.active : ''}
                  >
                    {currentPhase?.name
                    || (etaiement?.phases.length > 0 && etaiement?.phases[0]?.name)
                    }
                    <div className={styles.icon}
                      onMouseOver={() => {
                        setSelectStep(true);
                      }}
                    >
                      {IoChevronDown({})}
                    </div>

                  </a>
                  : <div
                    className={styles['select-step']}
                    onMouseLeave={() => setSelectStep(false)}
                    onClick={() => setSelectStep(false)}
                  >
                    <DraggablePhaseTabs
                      phases={etaiement?.phases || []}
                      pathPrefix={`/etaiements/${id}`}
                      getIsActive={getIsActive}
                      isPreview={isPreview}
                      colorScale={colorScale}
                      tabDenominator={etaiement.phases.length}
                      getPhaseLabel={(phase, index) => phase.name || `Partie ${index + 1}`}
                      onReorder={handleReorderPhases}
                      layoutStyles={styles}
                      variant="dropdown"
                    />
                  </div>
                }
                </div>
              }
              {countPhases > 0 && countPhases < 3
              && <>
                <DraggablePhaseTabs
                  phases={etaiement?.phases || []}
                  pathPrefix={`/etaiements/${id}`}
                  getIsActive={getIsActive}
                  isPreview={isPreview}
                  colorScale={colorScale}
                  tabDenominator={etaiement.phases.length}
                  getPhaseLabel={(phase, index) => phase.name || `Partie ${index + 1}`}
                  onReorder={handleReorderPhases}
                  layoutStyles={styles}
                  variant="tabs"
                />
                </>
              }
              {!isPreview
                && etaiement?.worksite?.name
                && etaiement?.worksite?.city
                && etaiement?.worksite?.department
                && etaiement?.logistics?.operationalBase
              && <>
                {!addStep
                  ? <a className={styles.add} onMouseOver={() => setAddStep(true)}>
                    {BiPlus({})} Ajouter une partie
                  </a>
                  : <div
                    className={styles['add-step']}
                    onMouseLeave={() => setAddStep(false)}
                  >
                    <div className={styles.select}>
                      <div className={styles.label}>
                      {BiPlus({})} Ajouter une partie
                    </div>
                    <div className={styles.option} onClick={() => handleCreatePhase()}>
                      Partie {etaiement.phases.length + 1}
                    </div>
                    {currentPhase?._id
                    && <div className={styles.option} onClick={() => handleDuplicatePhase()}>
                      Dupliquer cette partie
                    </div>
                    }
                  </div>
                </div>}
                </>
              }
            </div>
            {!generalInfosActive && currentPhase && etaiement?.logistics?.operationalBase && (
              <PhaseHeaderRates
                logistics={etaiement.logistics}
                transport={etaiement.transport}
                hourlyRate={etaiement.hourlyRate}
                agencyId={etaiement.agency?._id}
                agencies={list.agencies}
              />
            )}
            <div className={styles.tabs}>
              <Routes>
                <Route path='/' element={<GeneralInfos isPreview={isPreview} />} />
                <Route path='/:phaseId' element={<Phase isPreview={isPreview} />} />
              </Routes>
            </div>
            {!isPreview
                && etaiement?.worksite?.name
                && etaiement?.worksite?.city
                && etaiement?.worksite?.department
                && etaiement?.logistics?.operationalBase
                && <>
                  {!addStepDown
                    ? <a className={styles.addDown} onMouseOver={() => setAddStepDown(true)}>
                    {BiPlus({})} Ajouter une partie
                  </a>
                    : <div
                    className={styles['addDown-step']}
                    onMouseLeave={() => setAddStepDown(false)}
                  >
                    <div className={styles.select}>
                      <div className={styles.option} onClick={() => handleCreatePhase()}>
                        Partie {etaiement.phases.length + 1}
                      </div>
                      {currentPhase?._id
                      && <div className={styles.option} onClick={() => handleDuplicatePhase()}>
                        Dupliquer cette partie
                      </div>
                      }
                      <div className={styles.label}>
                        {BiPlus({})} Ajouter une partie
                      </div>
                    </div>
                  </div>
                  }
                </>
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default Etaiement;
